<template>
  <div v-bind:class="{loading: isLoading}" id="advanced-search">
    <div class="columns">
      <div class="column search-params">
        <label class="label">Select By Colors</label>
        <p class="control select-group">
          <template v-for="c, i in colors">
            <label class="checkbox">
              <input type="checkbox" :value="c.name" v-model="searchColors" v-if="c.type !== null">
              <span class="color-label" :style="'color: ' + c.foreground_color + '; background-color: ' + c.background_color + ';'">{{ c.name }}</span>
            </label><br class="color-break" v-if="(i + 1) % 5 === 0">
          </template>
        </p>
        <label class="label">Select By Rarity</label>
        <p class="control select-group">
          <label v-for="r in rarities" class="checkbox">
            <input type="checkbox" :value="r.value" v-model="searchRarity">
            <span class="color-label" :style="'color: ' + r.foreground_color + '; background-color: ' + r.background_color + ';'">{{ r.name }}</span>
          </label>
        </p>
        <label class="label">Select By Condition</label>
        <p class="control select-group">
          <label v-for="c, i in conditions" class="checkbox">
            <input type="checkbox" :value="c" v-model="searchCondition">
            {{ c }}
          </label>
        </p>
        <p class="control">
          <input class="input" id="name" type="text" v-model="searchName" placeholder="Card Name">
          <span class="select">
            <select style="width: 100%;" v-model="searchSet">
              <option value="">Card Set</option>
              <option v-for="s in sets" :value="s.uuid">{{ s.name }}</option>
            </select>
          </span>
        </p>
        <p class="control">
          <button @click="loadData" class="button is-primary">Search</button>
        </p>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <h2 class="subtitle">Search Results</h2>
        <div v-if="notification" class="notification is-info">
          <button @click="notification = ''" class="delete"></button>
          {{ notification }}
        </div>
        <table class="table">
          <thead>
            <tr>
              <th width="50"></th>
              <th width="75">Qty</th>
              <th class="clickable" width="75" @click="sortBy('available-quantity')">Avail</th>
              <th class="clickable" @click="sortBy('product-title')">Name</th>
              <th class="clickable" @click="sortBy('set-name')">Set</th>
              <th class="clickable" @click="sortBy('condition')">Cond</th>
              <th class="clickable" @click="sortBy('price')">Price</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in orderedProducts">
              <td><button :disabled="p.quantity < 1 || p.quantity > p.available_quantity" @click.prevent="addToCart(p)" class="button is-primary"><i class="fa fa-cart-plus" aria-hidden="true"></i></button></td>
              <td><input class="input quantity" type="number" v-model.number="p.quantity"></td>
              <td>{{ p.available_quantity }}</td>
              <td><card-title :title="cardName(p.variant.id)" :image-url="p.image"></card-title></td>
              <td>{{ setName(p.variant.id) }}</td>
              <td>{{ p.variant.title }}</td>
              <td>${{ p.variant.price}}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Client from 'shopify-buy';
import Raven from 'raven-js';
import CardTitle from './partials/CardTitle';
import processProduct from '../process-product';
import { variantId } from '../shopify-ids';

export default {
  name: 'BuySearch',
  data() {
    return {
      isLoading: false,
      products: [],
      checkout: {},
      client: null,
      cardDetails: [],

      sets: [],
      colors: [],
      rarities: [],
      conditions: [],

      searchName: '',
      searchSet: '',
      searchColors: [],
      searchRarity: [],
      searchCondition: [],

      notification: '',

      sortKey: '',
      sortDirection: 1, // -1 for reverse

      host: 'https://inventory.powernine.com/',
    };
  },
  components: {
    'card-title': CardTitle,
  },
  computed: {
    orderedProducts() {
      if (!this.sortKey) {
        return this.products;
      }

      const sk = this.sortKey;
      const getValue = (k, o) => {
        if (k === 'available-quantity') {
          return o.available_quantity;
        } else if (k === 'product-title') {
          return o.variant.productTitle;
        } else if (k === 'set-name') {
          return this.setName(o.variant.id);
        } else if (k === 'condition') {
          return o.variant.title;
        } else if (k === 'price') {
          return Number(o.variant.formattedPrice.substr(1));
        }

        return '';
      };

      return this.products.sort((a, b) => {
        if (getValue(sk, a) < getValue(sk, b)) {
          return 1 * this.sortDirection;
        } else if (getValue(sk, a) > getValue(sk, b)) {
          return -1 * this.sortDirection;
        }

        return 0;
      });
    },
  },
  methods: {
    addToCart(p) {
      this.client.checkout.addLineItems(
        localStorage.getItem('shopifyCheckoutId'),
        [{ variantId: p.variant.id, quantity: p.quantity }],
      );

      const cd = this.cardDetails.find(d => variantId(d.variant_id) === p.variant.id);
      this.notification = `${cd.card_name} has been added to your shopping cart.`;
    },
    sortBy(key) {
      this.sortKey = key;
      this.sortDirection = this.sortDirection * -1;
    },
    cardName(v) {
      const cd = this.cardDetails.find(d => variantId(d.variant_id) === v);
      if (cd !== undefined) {
        return cd.card_name;
      }

      return '';
    },
    setName(v) {
      const cd = this.cardDetails.find(d => variantId(d.variant_id) === v);
      if (cd !== undefined) {
        return cd.set_name;
      }

      return '';
    },
    loadData() {
      this.isLoading = true;
      this.products = [];

      const out = [];

      this.searchColors.forEach((c) => {
        out.push(`colors[]=${encodeURIComponent(c)}`);
      });
      this.searchRarity.forEach((r) => {
        out.push(`rarity[]=${encodeURIComponent(r)}`);
      });
      this.searchCondition.forEach((c) => {
        out.push(`conditions[]=${encodeURIComponent(c)}`);
      });
      if (this.searchName) {
        out.push(`name=${this.searchName}`);
      }
      if (this.searchSet) {
        out.push(`set=${this.searchSet}`);
      }

      const query = out.join('&');

      this.axios.get(
        `${this.host}shopify/variants?advanced_search=true&${query}`,
      ).then((response) => {
        this.cardDetails = response.data;

        const cardsCopy = response.data.slice();

        const processProducts = (products) => {
          products.forEach(processProduct(this.cardDetails, this.products));
        };

        while (cardsCopy.length) {
          const part = cardsCopy.splice(0, 30);

          this.client.product.fetchMultiple(
            part.map(p => btoa(`gid://shopify/Product/${p.product_id}`)),
          ).then(processProducts);
        }

        return this.client.checkout.fetch(localStorage.getItem('shopifyCheckoutId'));
      }).then((checkout) => {
        this.checkout = checkout;
        this.isLoading = false;
      })
      .catch((err) => {
        this.isLoading = false;
        throw err;
      });
    },
  },
  mounted() {
    this.client = Client.buildClient({
      storefrontAccessToken: 'c532a34d42a102560faf84bbb93624f1',
      domain: 'powernine.myshopify.com',
      appId: '6',
    });

    this.isLoading = true;

    new Promise((resolve) => {
      if (localStorage.getItem('shopifyCheckoutId')) {
        resolve(this.client.checkout.fetch(localStorage.getItem('shopifyCheckoutId')));
      }

      resolve(null);
    }).then((checkout) => {
      if (!checkout) {
        return this.client.checkout.create();
      }

      return checkout;
    }).then((checkout) => {
      localStorage.setItem('shopifyCheckoutId', checkout.id);
    }).then(() => this.axios.get(`${this.host}shopify/sets`))
    .then((response) => {
      this.sets = response.data;
      return this.axios.get(`${this.host}shopify/colors`);
    })
    .then((response) => {
      this.colors = response.data;
      return this.axios.get(`${this.host}shopify/conditions`);
    })
    .then((response) => {
      this.conditions = response.data;
      return this.axios.get(`${this.host}shopify/rarities`);
    })
    .then((response) => {
      this.rarities = response.data;
      this.isLoading = false;
    })
    .catch((err) => {
      this.isLoading = false;
      Raven.captureException(err);
      throw err;
    });
  },
  watch: {
    // eslint-disable-next-line
    '$route': function (to) {
      this.loadData(to.query.card_name);
    },
  },
};
</script>

<style scoped>
  .search-params p {
    margin-bottom: .25rem;
  }

  .search-params label.checkbox {
    margin-right: .5rem;
  }

  .search-params input.input#name {
    width: 20rem;
  }

  label.checkbox {
    width: 11rem;
    display: inline-block;
  }

  label.checkbox input {
    margin-right: 0;
  }

  span.color-label {
    width: 8rem;
    padding-left: .25rem;
    display: inline-block;
  }

  p.control.select-group {
    padding-left: 3rem;
  }

  @media
  only screen and (max-width: 760px),
  (min-device-width: 768px) and (max-device-width: 1024px)  {
    p.control.select-group {
      padding-left: .5rem;
    }

    br.color-break {
      display: none;
    }
  }
</style>



// WEBPACK FOOTER //
// BuyAdvancedSearch.vue?3b77b0d0